import React, { createContext, PropsWithChildren, useReducer } from "react";
import * as Sentry from "@sentry/react";
import TransferDestination, { DestinationType } from "manifest-components/src/classes/transferDestination";
import TransferInstruction from "manifest-components/src/classes/transferInstruction";
import Session from "../../utils/session";
import TransferStatusReducer from "./transferStatusReducer";
import { Actions } from "./transferStatusActions";
import { TransferStatusState } from "./transferStatusState";
import API from "../../utils/api";

export type TransferStatusProviderValue = ReturnType<typeof createProviderValue>;

export const localAPI = {
  getDestinationAccountsByType: API.transfer.getDestinationAccountsByType,
  getTransferInstruction: API.transferInstruction.getTransferInstruction,
  connectDestinationToTransferInstruction: API.transferInstruction.connectDestinationToTransferInstruction,
  getProviderMailingDetails: API.transferInstruction.getProviderMailingDetails,
  confirmCheckMailedToProvider: API.transferInstruction.confirmCheckMailedToProvider,
  schedulePhoneCall: API.transferInstruction.schedulePhoneCall,
  createOrUpdateDestinationBasedOnYetToOpen: API.transferInstruction.createOrUpdateDestinationBasedOnYetToOpen,
  getDestinationByTransferId: API.transfer.getDestinationByTransferId,
  schedulePhoneCallZoho: API.transferInstruction.schedulePhoneCallZoho,
};

export type TransferStatusProviderAPIValue = typeof localAPI;

function createProviderValue({ api = localAPI } = {}) {
  const { userId, accessToken } = Session;
  const [state, dispatch] = useReducer(TransferStatusReducer, TransferStatusState);

  async function getTransferInstruction(transferInstructionId) {
    dispatch({
      type: Actions.FETCHING_CURRENT_TRANSFER_INSTRUCTION,
    });
    const transferInstruction = await api.getTransferInstruction({
      userId,
      transferInstructionId,
    });
    dispatch({
      payload: {
        data: transferInstruction,
        transferIdReceived: transferInstruction.transferInstructionId,
      },
      type: Actions.SET_CURRENT_TRANSFER_INSTRUCTION,
    });
    return transferInstruction;
  }

  // GET_ROTH_TRANSFERRED_DESTINATIONS
  async function getRothTransferredDestinations() {
    let rothDestinationsData: TransferDestination[] = [];
    dispatch({
      type: Actions.FETCHING_ROTH_TRANSFERRED_DESTINATIONS,
    });
    try {
      rothDestinationsData = await api.getDestinationAccountsByType({
        userId,
        destinationType: DestinationType.rothDestination,
      });
    } catch (e) {
      Sentry.captureMessage("Unable to fetch roth transfer destination");
      rothDestinationsData = [];
    }
    dispatch({
      payload: rothDestinationsData,
      type: Actions.GET_ROTH_TRANSFERRED_DESTINATIONS,
    });
  }

  // CONNECT DESTINATION TO TRANSFER
  async function connectDestinationToTransferInstruction(params: {
    transferInstructionId: string;
    destination: TransferDestination;
  }) {
    const { transferInstructionId, destination } = params;
    try {
      await api.connectDestinationToTransferInstruction({
        destination,
        userId: Session.userId,
        transferInstructionId,
      });
      dispatch({
        type: Actions.CONNECT_DESTINATION_TO_TRANSFER,
      });
    } catch (e) {
      Sentry.captureMessage("Unable add destination to transfer instruction");
    }
    return getTransferInstruction(transferInstructionId);
  }

  // GET Provider Mailing Details
  async function getProviderMailingDetails(transferInstructionId) {
    dispatch({
      type: Actions.FETCHING_PROVIDER_MAILING_DETAILS,
    });
    try {
      const response = await api.getProviderMailingDetails({
        userId,
        transferInstructionId,
      });
      dispatch({
        payload: response,
        type: Actions.GET_PROVIDER_MAILING_DETAILS,
      });
    } catch (e) {
      Sentry.captureMessage("Unable to get provider mailing details");
    }
  }

  async function confirmCheckMailedToProvider(transferInstructionId: string | number) {
    try {
      await api.confirmCheckMailedToProvider({
        userId: Session.userId,
        transferInstructionId: transferInstructionId.toString(),
        accessToken,
      });
      dispatch({
        type: Actions.MAIL_CONFIRMED,
      });
    } catch (e) {
      throw new Error("Confirm Mail Failed");
    }
  }

  async function schedulePhoneCall(params: { transferInstructionId: number; eventUrl: string }) {
    return api.schedulePhoneCall({ ...params, userId });
  }

  async function schedulePhoneCallZoho(params: { transferInstructionId: number }) {
    return api.schedulePhoneCallZoho({ transferInstructionId: params.transferInstructionId, userId });
  }

  async function createOrUpdateDestinationBasedOnYetToOpen(params: {
    transferInstructionId: string;
    destination: TransferDestination;
    destinationType?: DestinationType;
  }) {
    try {
      return await api.createOrUpdateDestinationBasedOnYetToOpen({
        ...params,
        userId: Session.userId,
      });
    } catch (error) {
      throw new Error(`${error}`);
    }
  }

  async function getDestinationByTransferId(transferId: string | number) {
    return await api.getDestinationByTransferId({
      userId: Session.userId,
      transferId,
    });
  }

  async function getTransferInstructionById(transferInstructionId: string) {
    return await api.getTransferInstruction({ userId: Session.userId, transferInstructionId });
  }

  async function updateTransferInstruction(transferInstruction: TransferInstruction) {
    dispatch({
      payload: {
        data: transferInstruction,
        transferIdReceived: transferInstruction.transferInstructionId,
      },
      type: Actions.SET_CURRENT_TRANSFER_INSTRUCTION,
    });
  }

  return {
    ...state,
    getTransferInstruction,
    getRothTransferredDestinations,
    connectDestinationToTransferInstruction,
    confirmCheckMailedToProvider,
    getProviderMailingDetails,
    schedulePhoneCall,
    createOrUpdateDestinationBasedOnYetToOpen,
    getDestinationByTransferId,
    schedulePhoneCallZoho,
    getTransferInstructionById,
    updateTransferInstruction,
  };
}

export const TransferStatusContext = createContext({} as TransferStatusProviderValue);

const TransferStatusProvider = (props: PropsWithChildren<any>) => {
  return (
    <TransferStatusContext.Provider value={createProviderValue(props)}>{props.children}</TransferStatusContext.Provider>
  );
};

export default TransferStatusProvider;
